import { useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import type { UUID } from "../../../__definitions__/iconfinder.changeengine.com-definitions";
import { convertToLettersOnly } from "../../../utils/convert-to-letters-only";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import type { DisplayConditionData } from "../components/display-condition-dialog";
import {
  type DisplayConditionCallbackContext,
  getDisplayConditionDialogState,
} from "../utils/display-condition-nesting";

export type DisplayConditionRequestData = Partial<DisplayConditionData> & {
  id?: string | number;
  context: DisplayConditionCallbackContext;
};

type DisplayConditionDialogState = {
  nestedBlocked: boolean;
  selectedId?: UUID;
};

export type DisplayConditionReturn = {
  isOpen: boolean;
  onDisplayConditionRequest: (
    data: DisplayConditionRequestData,
    done: (data: DisplayConditionData) => void,
  ) => void;
  onConfirm: (data: DisplayConditionData) => void;
  onClose: () => void;
  selectedId?: UUID;
  nestedBlocked: boolean;
};

/**
 * Manages the bridge between Unlayer's imperative callback
 * and React's declarative Modal state.
 */
export const useDisplayCondition = (): DisplayConditionReturn => {
  const {
    open,
    handleClose,
    handleOpen,
    data: dialogState,
  } = useDialog<DisplayConditionDialogState>();
  const { setValue, getValues } = useFormContext<MomentFormMoment>();

  const doneCallbackRef = useRef<((data: DisplayConditionData) => void) | null>(null);

  const onDisplayConditionRequest = useCallback(
    (dcData: DisplayConditionRequestData, done: (data: DisplayConditionData) => void) => {
      doneCallbackRef.current = done;
      handleOpen(getDisplayConditionDialogState(getValues("email_moment.design"), dcData));
    },
    [getValues, handleOpen],
  );

  const onConfirm = useCallback(
    (conditionData: DisplayConditionData) => {
      if (doneCallbackRef.current) {
        const currentConditions = getValues("dynamic_segment_id_map");
        setValue("dynamic_segment_id_map", {
          ...currentConditions,
          [conditionData.id]: convertToLettersOnly(conditionData.id),
        });

        doneCallbackRef.current(conditionData);
        doneCallbackRef.current = null;
      }
      handleClose();
    },
    [handleClose, getValues, setValue],
  );

  const onClose = useCallback(() => {
    handleClose();
    doneCallbackRef.current = null;
  }, [handleClose]);

  return {
    isOpen: open,
    onDisplayConditionRequest,
    onConfirm,
    onClose,
    selectedId: dialogState?.selectedId,
    nestedBlocked: dialogState?.nestedBlocked ?? false,
  };
};
